// Source for the script that runs *inside* the sandboxed iframe. Kept as a
// string because the iframe loads via srcdoc — it cannot import. The iframe
// has `sandbox="allow-scripts"` only, so its origin is null: no DOM, cookie,
// localStorage, or session-token access in the parent is reachable. The only
// outbound channel is postMessage to the parent, which serves the `pp.*` API.
export const sandboxSource = `
(() => {
  const pending = new Map();
  const post = (msg) => parent.postMessage(msg, '*');

  const safeSerialize = (v) => {
    try { return JSON.parse(JSON.stringify(v === undefined ? null : v)); }
    catch { return String(v); }
  };

  const pp = {
    batch: (ops) => new Promise((resolve, reject) => {
      if (!Array.isArray(ops)) {
        reject(new Error('pp.batch expects an array of ops'));
        return;
      }
      const id = String(Math.random()).slice(2) + ':' + Date.now();
      pending.set(id, { resolve, reject });
      post({ type: 'batch', id, ops });
    }),
    log: (...args) => {
      const serializable = safeSerialize(args);
      post({ type: 'log', args: Array.isArray(serializable) ? serializable : [serializable] });
    },
    getOutline: () => pp.batch([{ op: 'getOutline' }]).then((r) => r[0]),
    addSlide: () => pp.batch([{ op: 'addSlide' }]).then((r) => r[0]),
    getSelection: () => pp.batch([{ op: 'getSelection' }]).then((r) => r[0]),
    setFirstShapeText: (slide, text) =>
      pp.batch([{ op: 'setFirstShapeText', slide: slide, text: text }]).then((r) => r[0]),
    countSlides: () => pp.batch([{ op: 'countSlides' }]).then((r) => r[0]),
    getSlideContents: (slide) =>
      pp.batch([{ op: 'getSlideContents', slide: slide }]).then((r) => r[0]),
    // Generic Office.js forwarder — escape hatch for anything not covered
    // by the typed ops above. Path is rooted at context.presentation.
    // Examples:
    //   await pp.get(['slides'], 'items/id,items/index')
    //   await pp.get(['slides', {method:'getItemAt', args:[0]}, 'shapes'], 'items/id,items/name,items/type')
    //   await pp.call(['slides', {method:'getItemAt', args:[0]}, 'shapes', {method:'addTextBox', args:['Hi', {left:100,top:100,width:300,height:80}]}])
    //   await pp.set(['slides', {method:'getItemAt', args:[0]}, 'shapes', {method:'getItemAt', args:[0]}, 'name'], 'Logo')
    get: (path, load) =>
      pp.batch([{ op: 'oj.get', path: path, load: load }]).then((r) => r[0]),
    call: (path, load) =>
      pp.batch([{ op: 'oj.call', path: path, load: load }]).then((r) => r[0]),
    set: (path, value) =>
      pp.batch([{ op: 'oj.set', path: path, value: value }]).then((r) => r[0]),
    // Returns { base64, mediaType, slideIndex }. Note: the base64 PNG can be
    // large — prefer the peek_slide top-level tool for vision, which routes
    // the image natively to the model. Useful here only for sandbox-side
    // debugging via pp.log.
    getSlideImage: (slide, opts) =>
      pp.batch([{ op: 'getSlideImage', slide: slide, width: opts && opts.width, height: opts && opts.height }]).then((r) => r[0]),
    // High-throughput bulk shape builder. Pass an array of declarative shape
    // specs; the parent creates and styles them all in one PowerPoint.run,
    // syncing in small chunks so Mac PowerPoint doesn't crash under proxy
    // load. ALWAYS prefer this over loops of oj.set/oj.call when building a
    // slide. Returns { slideIndex, ids }.
    addShapes: (slide, shapes) =>
      pp.batch([{ op: 'addShapes', slide: slide, shapes: shapes }]).then((r) => r[0]),
    // insertSlidesFromBase64 is intentionally NOT exposed from sandbox code:
    // base64 strings shouldn't flow through model-generated code. The
    // insert_deck_slides tool dispatches the op directly via the parent.
    deleteScratchSlides: (scratchTag) =>
      pp.batch([{ op: 'deleteScratchSlides', scratchTag: scratchTag }]).then((r) => r[0]),
    listScratchSlides: (scratchTag) =>
      pp.batch([{ op: 'listScratchSlides', scratchTag: scratchTag }]).then((r) => r[0]),
  };

  window.addEventListener('message', (event) => {
    const msg = event.data;
    if (!msg || typeof msg !== 'object') return;
    if (msg.type === 'batchResult') {
      const p = pending.get(msg.id);
      if (!p) return;
      pending.delete(msg.id);
      if (msg.ok) p.resolve(msg.results);
      else {
        // Preserve structured BridgeError fields (code, hint, nextStep, …)
        // through the Promise rejection so the parent can return them to the model.
        const e = msg.error || {};
        const err = new Error(e.message || 'Batch failed');
        err.name = e.name || 'Error';
        err.bridgeError = e;
        p.reject(err);
      }
      return;
    }
    if (msg.type === 'run') {
      let fn;
      try {
        fn = new Function('pp', '"use strict"; return (async () => { ' + msg.code + ' })();');
      } catch (err) {
        post({
          type: 'error',
          error: {
            name: (err && err.name) ? String(err.name) : 'SyntaxError',
            message: (err && err.message) ? String(err.message) : String(err),
          },
        });
        return;
      }
      Promise.resolve()
        .then(() => fn(pp))
        .then((value) => post({ type: 'done', value: safeSerialize(value) }))
        .catch((err) => {
          if (err && err.bridgeError && typeof err.bridgeError === 'object') {
            post({ type: 'error', error: err.bridgeError });
            return;
          }
          post({
            type: 'error',
            error: {
              name: (err && err.name) ? String(err.name) : 'Error',
              message: (err && err.message) ? String(err.message) : String(err),
              stack: (err && err.stack) ? String(err.stack) : undefined,
            },
          });
        });
    }
  });

  post({ type: 'ready' });
})();
`;
